/**
 * Progressive Booking Flow Validation Schemas
 *
 * Zod schemas for validating booking section data.
 * Validation occurs on section completion per user decisions.
 */

import { z } from 'zod';
import type {
  VenueSelectionData,
  DateTimeSelectionData,
  DurationSelectionData,
  GuestDetailsData,
  ReviewData,
  ProgressiveBookingFormData,
} from '../types/progressive-booking';

/**
 * Step 1: Venue & Activity Selection Schema
 * Validates venue, activity, and rate IDs are valid UUIDs
 */
export const venueSelectionSchema = z.object({
  venueId: z.string().uuid().describe('Select a venue'),
  activityId: z.string().uuid().describe('Choose an activity type'),
  rateId: z.string().uuid().describe('Select a pricing rate'),
}) satisfies z.ZodType<VenueSelectionData>;

/**
 * Step 2: Date & Time Selection Schema
 * Validates date is ISO format, times are HH:mm, and endTime > startTime
 */
export const dateTimeSelectionSchema = z
  .object({
    date: z.string().datetime().describe('Select booking date'),
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Start time must be in HH:mm format')
      .describe('Select start time'),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'End time must be in HH:mm format')
      .describe('Select end time'),
  })
  .refine(
    (data) => {
      // Parse times and compare
      const [startHour, startMin] = data.startTime.split(':').map(Number);
      const [endHour, endMin] = data.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    }
  ) satisfies z.ZodType<DateTimeSelectionData>;

/**
 * Step 3: Duration Selection Schema
 * Validates duration is between 1-24 hours
 */
export const durationSelectionSchema = z.object({
  durationHours: z
    .number()
    .int('Duration must be a whole number')
    .min(1, 'Minimum 1 hour required')
    .max(24, 'Maximum 24 hours allowed')
    .describe('Select booking duration'),
}) satisfies z.ZodType<DurationSelectionData>;

/**
 * Step 4: Guest Details Schema
 * Validates guest name, Indian mobile number, guest count, and optional notes
 *
 * Guest name and phone are optional for single guests (guestCount = 1)
 * but required for group bookings (guestCount > 1)
 */
export const guestDetailsSchema = z
  .object({
    guestName: z
      .string()
      .max(100, 'Name must be at most 100 characters')
      .optional()
      .default('')
      .describe('Enter guest name'),
    guestPhone: z.string().optional().default('').describe('Enter mobile number'),
    guestCount: z
      .number()
      .int('Guest count must be a whole number')
      .min(1, 'At least 1 guest required')
      .max(50, 'Maximum 50 guests allowed')
      .describe('Number of guests'),
    notes: z.string().max(500, 'Notes must be at most 500 characters').optional(),
  })
  .refine(
    (data) => {
      // Only require name/phone validation for group bookings
      if (data.guestCount > 1) {
        const hasValidName = data.guestName && data.guestName.length >= 2;
        const hasValidPhone = data.guestPhone && /^[6-9]\d{9}$/.test(data.guestPhone);
        return hasValidName && hasValidPhone;
      }
      return true; // No validation required for single guest
    },
    {
      message: 'Guest name (min 2 characters) and valid phone number required for group bookings',
      path: ['guestName'],
    }
  ) satisfies z.ZodType<GuestDetailsData>;

/**
 * Step 5: Review Schema
 * Validates optional coupon code
 */
export const bookingReviewSchema = z.object({
  couponCode: z.string().max(20, 'Coupon code must be at most 20 characters').optional(),
}) satisfies z.ZodType<ReviewData>;

/**
 * Booking Session State Schema
 * Validates session persistence structure with TTL
 */
export const bookingSessionSchema = z.object({
  data: z.record(z.unknown()),
  timestamp: z.string().datetime('Invalid timestamp format'),
  expiresAt: z.string().datetime('Invalid expiration timestamp format'),
  completedSections: z.array(z.enum(['venue', 'datetime', 'duration', 'guests', 'review'])),
  currentSection: z.enum(['venue', 'datetime', 'duration', 'guests', 'review']),
});

/**
 * Progressive Booking Form Schema
 * Combined schema for final submission validation
 * Merges all section schemas for API submission
 *
 * Guest name and phone are optional for single guests but required for groups
 */
export const progressiveBookingFormSchema = z
  .object({
    // Step 1: Venue
    venueId: z.string().uuid(),
    activityId: z.string().uuid(),
    rateId: z.string().uuid(),

    // Step 2: DateTime
    date: z.string().datetime(),
    startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),

    // Step 3: Duration
    durationHours: z.number().int().min(1).max(24),

    // Step 4: Guests (name/phone optional for single guest)
    guestName: z.string().max(100).optional().default(''),
    guestPhone: z.string().optional().default(''),
    guestCount: z.number().int().min(1).max(50),
    notes: z.string().max(500).optional(),

    // Step 5: Review
    couponCode: z.string().max(20).optional(),
  })
  .refine(
    (data) => {
      // Require name/phone for group bookings
      if (data.guestCount > 1) {
        const hasValidName = data.guestName && data.guestName.length >= 2;
        const hasValidPhone = data.guestPhone && /^[6-9]\d{9}$/.test(data.guestPhone);
        return hasValidName && hasValidPhone;
      }
      return true;
    },
    {
      message: 'Guest name and phone required for group bookings',
      path: ['guestName'],
    }
  ) satisfies z.ZodType<ProgressiveBookingFormData>;
